import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  ActionItem,
  FollowUpAction,
  FollowUpActionForm,
  MutationError,
} from 'src/types/myFollowUp';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import type { FilterValue } from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { FILTER_KEYS } from 'src/components/followUp/hooks/constants';
import { EMPTY_ACTION_ITEM } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/constants';
import type { DateRangeAsStrings } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';
import { dateRangeFilterToApiString } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';

export const useMyFollowUpFilters = () => {
  const { filterGroups } = useFilterContext();

  const dateRange = useMemo(
    () =>
      filterGroups[FILTER_KEYS.DATE_RANGE]?.[0]
        ? dateRangeFilterToApiString(
            filterGroups[
              FILTER_KEYS.DATE_RANGE
            ][0] as FilterValue<DateRangeAsStrings>,
          )
        : undefined,
    [filterGroups[FILTER_KEYS.DATE_RANGE]],
  );

  const appliedFilters = useMemo(() => {
    const filters = ['search', 'survey', 'followUpOwner', 'status'];
    return Object.fromEntries(
      filters.map((filterKey) => [
        filterKey,
        filterGroups[filterKey]
          ?.map(({ filterExpr }) => Object.values(filterExpr))
          .toString(),
      ]),
    );
  }, [filterGroups]);

  return useMemo(
    () => ({ dateRange, appliedFilters }),
    [dateRange, appliedFilters],
  );
};

export const useActionItemListQuery = (
  offset: number,
  ordering: string,
  limit = 10,
  enabled = true,
) => {
  const { dateRange, appliedFilters } = useMyFollowUpFilters();

  return useQuery<
    {
      count: number;
      results: ActionItem[];
      next: string;
      previous: string;
    },
    string
  >({
    queryKey: [
      apiPaths.MY_FOLLOW_UP.LIST,
      offset,
      ordering,
      limit,
      dateRange,
      appliedFilters,
    ],
    enabled,
    queryFn: ({ signal }) =>
      HttpService.get(
        apiPaths.MY_FOLLOW_UP.LIST,
        {
          limit,
          offset,
          ordering:
            ordering === 'follow_up_owner'
              ? 'follow_up_owner__first_name, follow_up_owner__last_name'
              : ordering === '-follow_up_owner'
                ? '-follow_up_owner__first_name, -follow_up_owner__last_name'
                : ordering,
          ...dateRange,
          ...appliedFilters,
        },
        true,
        { signal },
      )
        .then((data) => Promise.resolve(data.data))
        .catch(({ response }) => {
          if (
            response.status >= 500 ||
            response.status === 424 ||
            response.status === 404 ||
            response.status === 400
          ) {
            return Promise.reject('somethingWentWrong');
          }
          if (Object.hasOwn(response.data, 'detail')) {
            return Promise.reject(String(response.data.detail));
          }
          return Promise.reject(String(response.data));
        }),
  });
};

export const useActionItemDetailQuery = (
  actionItemId: number | null,
  actionItemBeingCreated?: Partial<FollowUpAction>,
) => {
  const endpoint = useMemo(
    () =>
      generatePath(apiPaths.FOLLOW_UP_ACTIONS.DETAIL, {
        actionItemId: actionItemId || 0,
      }),
    [actionItemId],
  );
  const { data, isFetching, refetch, error } = useQuery<FollowUpAction>({
    queryKey: [endpoint],
    enabled: !!actionItemId,
    cacheTime: 0,
    queryFn: ({ signal }) =>
      HttpService.get(endpoint, {}, true, { signal })
        .then(({ data }) => {
          if (!data) return Promise.reject('no data');

          return Promise.resolve(data);
        })
        .catch(({ response }) => Promise.reject(response.status)),
  });

  let actionItem: FollowUpAction | undefined = data;
  if (!actionItem && actionItemId === 0) {
    actionItem = { ...EMPTY_ACTION_ITEM, ...actionItemBeingCreated };
  }

  return {
    actionItem,
    isFetching,
    refetch,
    error,
  };
};

export const useActionItemEditQuery = (actionItemId: number | null) => {
  const isCreate = !actionItemId;
  const endpoint = useMemo(
    () =>
      isCreate
        ? generatePath(apiPaths.FOLLOW_UP_ACTIONS.CREATE)
        : generatePath(apiPaths.FOLLOW_UP_ACTIONS.DETAIL, {
            actionItemId: actionItemId || 0,
          }),
    [actionItemId, isCreate],
  );
  const httpService = isCreate ? HttpService.post : HttpService.patch;
  const { data, error, isSuccess, reset, ...rest } = useMutation<
    FollowUpAction,
    MutationError,
    Partial<FollowUpActionForm>
  >({
    mutationFn: (payload) =>
      httpService(endpoint, payload, {}, true)
        .then((data) => Promise.resolve(data.data))
        .catch(({ response }) => Promise.reject(response.data)),
  });

  return {
    actionItem: data,
    error,
    isSuccess,
    reset,
    ...rest,
  };
};
